import React, { useEffect, useRef, useState } from 'react';

type LazyLoadArgTypes = {
  children: JSX.Element;
  threshold: number;
  rootMargin: string;
  placeholderClassname: string;
};

/**
 * LazyLoad component that lazy loads children
 * @param {Object} props
 * @param {ReactElement} props.children - Component children that will be lazy loaded
 * @param {number} props.threshold - Indicates what percentage of the target's visibility the observer's callback should be executed
 * @param {string} props.rootMargin - Margin around the root
 * @param {string} props.placeholderClassname - placeholder class name (before lazy loading happens)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API for more info on
 * `rootMargin` and `threshold`
 */
const LazyLoad = ({ children, threshold, rootMargin, placeholderClassname }: LazyLoadArgTypes): JSX.Element => {
  // React refs
  const observer = useRef<IntersectionObserver | null>(null);
  const el = useRef<HTMLDivElement | null>(null);

  // React state
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    if (window) {
      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          if (el.current && observer.current) {
            observer.current.unobserve(el.current);
          }
          setIsVisible(true);
        }
      }, options);

      // Start observing
      if (el.current) {
        observer.current.observe(el.current);
      }
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  return isVisible ? children : <div ref={el} className={placeholderClassname} />;
};

LazyLoad.defaultProps = {
  threshold: 0.0,
  rootMargin: '50px',
  placeholderClassname: '',
};

export default LazyLoad;
